import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPersonalCollection } from '../../../api';

const CreateCollection = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        collection_name: '',
        collection_description: '',
        level: 'beginner',
        language: 'jp',
        translation_language: 'en'
    });
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const parseCSV = (text) => {
        const lines = text.split(/\r?\n/);
        if (lines.length < 2) return [];

        const header = lines[0].split(',').map(h => h.trim().toLowerCase());
        const result = [];

        // Simple CSV parser that handles quotes
        const parseLine = (line) => {
            const row = [];
            let inQuotes = false;
            let current = '';
            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                if (char === '"') {
                    inQuotes = !inQuotes;
                } else if (char === ',' && !inQuotes) {
                    row.push(current.trim());
                    current = '';
                } else {
                    current += char;
                }
            }
            row.push(current.trim());
            return row;
        };

        for (let i = 1; i < lines.length; i++) {
            if (!lines[i].trim()) continue;
            const values = parseLine(lines[i]);
            const card = {};
            
            // Map header to expected fields
            header.forEach((h, index) => {
                if (h === 'word') card.word = values[index];
                if (h === 'translation') card.translation = values[index];
                if (h === 'exampleusage') card.usage_example = values[index];
                if (h === 'exampletranslation') card.usage_example_translation = values[index];
            });

            if (card.word && card.translation) {
                result.push({
                    ...card,
                    index: result.length
                });
            }
        }
        return result;
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const parsedCards = parseCSV(event.target.result);
                setCards(parsedCards);
                setError(null);
            } catch (err) {
                setError('Failed to parse CSV file. Please check the format.');
            }
        };
        reader.readAsText(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.collection_name) {
            setError('Collection name is required');
            return;
        }
        if (cards.length === 0) {
            setError('Please upload a CSV file with at least one card');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const payload = {
                ...formData,
                cards: cards
            };
            await createPersonalCollection(payload);
            setSuccess(true);
            setTimeout(() => {
                navigate('/collections-list');
            }, 1500);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create collection');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            padding: '40px 20px',
            maxWidth: '800px',
            margin: '0 auto',
            fontFamily: "'Inter', system-ui, -apple-system, sans-serif"
        }}>
            <div style={{ marginBottom: '32px' }}>
                <button 
                    onClick={() => navigate('/collections-list')}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#64748b',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: 0,
                        marginBottom: '20px'
                    }}
                >
                    ← Back to Collections
                </button>
                <h1 style={{ fontSize: '2.25rem', fontWeight: '800', color: '#1e293b', margin: '0 0 8px 0' }}>Create New Collection</h1>
                <p style={{ color: '#64748b', fontSize: '1.1rem' }}>Enter details and upload your flashcards CSV</p>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '24px' }}>
                <div style={{ background: 'white', padding: '32px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'grid', gap: '20px' }}>
                        <div>
                            <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#334155' }}>Collection Name*</label>
                            <input
                                type="text"
                                name="collection_name"
                                value={formData.collection_name}
                                onChange={handleInputChange}
                                placeholder="e.g. My Japanese Vocabulary"
                                style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem' }}
                                required
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#334155' }}>Description</label>
                            <textarea
                                name="collection_description"
                                value={formData.collection_description}
                                onChange={handleInputChange}
                                placeholder="What is this collection about?"
                                style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', minHeight: '100px', resize: 'vertical' }}
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                            <div>
                                <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#334155' }}>Level</label>
                                <select
                                    name="level"
                                    value={formData.level}
                                    onChange={handleInputChange}
                                    style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', background: 'white' }}
                                >
                                    <option value="beginner">Beginner</option>
                                    <option value="intermediate">Intermediate</option>
                                    <option value="advanced">Advanced</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#334155' }}>Source Language</label>
                                <select
                                    name="language"
                                    value={formData.language}
                                    onChange={handleInputChange}
                                    style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', background: 'white' }}
                                >
                                    <option value="jp">Japanese (JP)</option>
                                    <option value="en">English (EN)</option>
                                    <option value="ua">Ukrainian (UA)</option>
                                    <option value="es">Spanish (ES)</option>
                                    <option value="fr">French (FR)</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#334155' }}>Translation Language</label>
                                <select
                                    name="translation_language"
                                    value={formData.translation_language}
                                    onChange={handleInputChange}
                                    style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', background: 'white' }}
                                >
                                    <option value="en">English (EN)</option>
                                    <option value="ua">Ukrainian (UA)</option>
                                    <option value="jp">Japanese (JP)</option>
                                    <option value="es">Spanish (ES)</option>
                                    <option value="fr">French (FR)</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ background: '#f8fafc', padding: '32px', borderRadius: '16px', border: '2px dashed #cbd5e1' }}>
                    <div style={{ textAlign: 'center' }}>
                        <h3 style={{ margin: '0 0 12px 0', color: '#334155' }}>Step 2: Upload Flashcards CSV</h3>
                        <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '20px' }}>
                            Format: <code>word,translation,exampleusage,exampletranslation</code>
                        </p>
                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleFileChange}
                            id="csv-upload"
                            style={{ display: 'none' }}
                        />
                        <label 
                            htmlFor="csv-upload"
                            style={{
                                display: 'inline-block',
                                background: '#334155',
                                color: 'white',
                                padding: '12px 24px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                transition: 'background 0.2s'
                            }}
                        >
                            Select CSV File
                        </label>
                        {cards.length > 0 && (
                            <div style={{ marginTop: '20px', color: '#166534', fontWeight: '600' }}>
                                ✓ {cards.length} cards detected
                            </div>
                        )}
                    </div>
                </div>

                {error && (
                    <div style={{ padding: '16px', background: '#fef2f2', color: '#991b1b', borderRadius: '8px', border: '1px solid #fecaca', fontWeight: '500' }}>
                        {error}
                    </div>
                )}

                {success && (
                    <div style={{ padding: '16px', background: '#f0fdf4', color: '#166534', borderRadius: '8px', border: '1px solid #bbf7d0', fontWeight: '500' }}>
                        Success! Redirecting to collections list...
                    </div>
                )}

                <button
                    type="submit"
                    disabled={loading || success}
                    style={{
                        background: (loading || success) ? '#94a3b8' : '#1e293b',
                        color: 'white',
                        padding: '16px',
                        borderRadius: '12px',
                        border: 'none',
                        fontSize: '1.1rem',
                        fontWeight: '700',
                        cursor: (loading || success) ? 'not-allowed' : 'pointer',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        marginTop: '12px'
                    }}
                >
                    {loading ? 'Creating...' : 'Create Collection'}
                </button>
            </form>
        </div>
    );
};

export default CreateCollection;
